/**
 * Route Definition Helpers for UI Plugins
 *
 * Reduces boilerplate when defining routes for UI plugins.
 * Auto-generates IDs, prefixes paths, and provides type safety.
 */
/**
 * Infer a display name from a route path
 */
function inferNameFromPath(path) {
    if (path === '/' || path === '')
        return 'Dashboard';
    // Get the last segment
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    // Skip dynamic segments
    if (lastSegment?.startsWith(':')) {
        const previousSegment = segments[segments.length - 2];
        if (previousSegment && !previousSegment.startsWith(':')) {
            return toTitleCase(previousSegment);
        }
        return 'Detail';
    }
    return toTitleCase(lastSegment || 'Dashboard');
}
/**
 * Convert kebab-case or camelCase to Title Case
 */
function toTitleCase(str) {
    return str
        .replace(/[-_]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
/**
 * Generate a route ID from plugin ID and path
 */
function generateRouteId(pluginId, path) {
    const pathPart = path
        .replace(/^\//, '')
        .replace(/\//g, '-')
        .replace(/:/g, '')
        .replace(/--+/g, '-')
        .replace(/-$/, '');
    return pathPart ? `${pluginId}-${pathPart}` : `${pluginId}-index`;
}
/**
 * Wrap a component in a lazy import if it's not already
 */
function ensureLazyComponent(component) {
    // If it's already a lazy import function, return as-is
    if (typeof component === 'function' && component.length === 0) {
        // Check if it returns a promise (lazy import pattern)
        const result = component();
        if (result && typeof result.then === 'function') {
            // It's a lazy import, return the original function
            return component;
        }
    }
    // Wrap the component in a lazy import function
    return () => Promise.resolve({ default: component });
}
/**
 * Define routes for a UI plugin with reduced boilerplate.
 *
 * @param pluginId - The plugin identifier
 * @param basePath - Base path for all routes (e.g., '/warehouse')
 * @param routes - Array of route definitions
 * @returns Array of compiled routes ready for registration
 *
 * @example
 * ```typescript
 * export const routes = defineRoutes('warehouse-ui', '/warehouse', [
 *   {
 *     path: '/',
 *     component: () => import('./pages/Dashboard').then(m => ({ default: m.WarehouseDashboard })),
 *     nav: { icon: 'Warehouse', showInNav: true, order: 30 }
 *   },
 *   {
 *     path: '/list',
 *     component: () => import('./pages/List').then(m => ({ default: m.WarehouseList })),
 *     nav: { icon: 'List', showInNav: true, order: 1 }
 *   },
 *   {
 *     path: '/:warehouseId/locations',
 *     component: () => import('./pages/Locations').then(m => ({ default: m.LocationsPage })),
 *   },
 * ]);
 * ```
 */
export function defineRoutes(pluginId, basePath, routes) {
    // Validate inputs
    if (!pluginId || typeof pluginId !== 'string') {
        throw new Error('pluginId must be a non-empty string');
    }
    if (!basePath || typeof basePath !== 'string') {
        throw new Error('basePath must be a non-empty string');
    }
    if (!basePath.startsWith('/')) {
        throw new Error(`basePath must start with /: ${basePath}`);
    }
    if (!Array.isArray(routes)) {
        throw new Error('routes must be an array');
    }
    // First pass: create all routes with IDs
    const compiledRoutes = routes.map((route, index) => {
        // Validate route
        if (!route.path && route.path !== '') {
            throw new Error(`Route at index ${index} must have a path`);
        }
        if (!route.component) {
            throw new Error(`Route at index ${index} must have a component`);
        }
        // Build full path
        const normalizedPath = route.path === '/' ? '' : route.path;
        const fullPath = basePath + normalizedPath;
        // Generate route ID
        const id = generateRouteId(pluginId, route.path);
        // Infer name if not provided
        const name = route.name || inferNameFromPath(route.path);
        return {
            id,
            pluginId,
            path: fullPath,
            component: ensureLazyComponent(route.component),
            name,
            description: route.description,
            requiresAuth: route.requiresAuth ?? true,
            permissions: route.permissions?.map((p) => typeof p === 'string' ? { pattern: p } : p),
            parentId: undefined, // Will be set in second pass
            metadata: {
                icon: route.nav?.icon,
                showInNav: route.nav?.showInNav ?? false,
                order: route.nav?.order ?? 0,
                description: route.description,
                audience: route.nav?.audience ?? 'all',
                ...(route.nav?.matchPaths ? { matchPaths: route.nav.matchPaths } : {}),
                ...(route.nav?.badge ? { navBadge: route.nav.badge } : {}),
            },
        };
    });
    // Second pass: resolve parentPath to parentId
    for (let i = 0; i < routes.length; i++) {
        const route = routes[i];
        if (route.parentPath !== undefined) {
            const parentId = generateRouteId(pluginId, route.parentPath);
            // Verify parent exists
            const parentExists = compiledRoutes.some((r) => r.id === parentId);
            if (!parentExists) {
                console.warn(`Parent route "${route.parentPath}" not found for route "${route.path}". ` +
                    `Make sure the parent is defined in the same defineRoutes call.`);
            }
            compiledRoutes[i].parentId = parentId;
        }
    }
    return compiledRoutes;
}
